import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { Truck, Package, Building2, PhoneCall, ArrowRight, Check } from "lucide-react";
import { CONTACT } from "@/constants/contact";
import { FleetEnquiryModal } from "@/components/FleetEnquiryModal";

const BENEFITS = [
  { icon: Truck, title: "Bulk fleet at scale", desc: "10 to 500+ vehicles, branded in your colours if you need." },
  { icon: Building2, title: "Enterprise SLAs", desc: "Uptime commitments, dedicated account manager, monthly reporting." },
  { icon: Package, title: "Delivery-ready models", desc: "High-payload cargo scooters and long-range motorcycles for last-mile logistics." },
  { icon: PhoneCall, title: "Priority support", desc: "24×7 fleet-priority helpline and a 45-minute SLA on roadside assistance." },
];

const CHECKS = [
  "GST invoices for every payout",
  "Monthly and quarterly billing options",
  "Fleet dashboard with utilisation reports",
  "Onboarding & rider training included",
  "Insurance & full-service maintenance covered",
];

const BusinessFleet = () => {
  const [enquiryOpen, setEnquiryOpen] = useState(false);

  return (
    <div className="min-h-screen bg-[#F8F9FA]" data-testid="business-fleet-page">
      <Navbar />

      {/* Hero */}
      <div className="relative overflow-hidden bg-[#0A0B10] text-white">
        <div className="absolute inset-0 grid-pattern opacity-25" />
        <div className="absolute inset-0 bg-gradient-to-r from-[#0A0B10] via-[#0A0B10]/80 to-transparent" />
        <div className="relative max-w-7xl mx-auto px-5 md:px-8 py-20">
          <p className="text-[10px] uppercase tracking-widest text-[#3DDC97] font-bold font-mono-num">— Business & Fleet</p>
          <h1 className="font-display font-extrabold text-4xl md:text-6xl mt-3 tracking-tight max-w-3xl">
            Electrify your fleet, <span className="brand-gradient">without owning it.</span>
          </h1>
          <p className="text-white/70 mt-5 max-w-2xl text-[15px] md:text-lg leading-relaxed">
            Long-term electric two-wheeler rentals for delivery businesses,
            hotels, resorts, corporates and startups. Pay for uptime, we handle
            the rest.
          </p>
          <div className="mt-8 flex flex-wrap gap-3">
            <button
              type="button"
              onClick={() => setEnquiryOpen(true)}
              data-testid="business-enquire-cta"
              className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-[#3DDC97] text-[#0A0B10] font-bold hover:opacity-90 transition-opacity"
            >
              Get a fleet quote <ArrowRight size={16} />
            </button>
            <a
              href={CONTACT.whatsapp("Hi eBikeGo team, I'd like to enquire about fleet rentals.")}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="business-whatsapp-cta"
              className="inline-flex items-center gap-2 px-6 py-3 rounded-full border border-white/20 text-white font-semibold hover:border-white/60 transition-colors"
            >
              Chat on WhatsApp
            </a>
          </div>
        </div>
      </div>

      {/* Benefits */}
      <section className="max-w-7xl mx-auto px-5 md:px-8 py-20">
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
          {BENEFITS.map((b, i) => (
            <div key={i} className="card-premium p-7">
              <div className="w-11 h-11 rounded-xl bg-brand-gradient flex items-center justify-center text-white">
                <b.icon size={20} />
              </div>
              <h3 className="font-display font-bold text-lg mt-4">{b.title}</h3>
              <p className="text-sm text-gray-600 mt-2 leading-relaxed">{b.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* What you get */}
      <section className="max-w-7xl mx-auto px-5 md:px-8 pb-20 grid lg:grid-cols-2 gap-10">
        <div>
          <p className="text-[10px] uppercase tracking-widest font-bold text-[#2D7FF9] font-mono-num">— What you get</p>
          <h2 className="font-display font-extrabold text-3xl md:text-4xl mt-3 tracking-tight">
            A managed EV programme, not just a rental.
          </h2>
          <p className="text-gray-600 mt-4 leading-relaxed max-w-md">
            We deploy, maintain, insure and swap vehicles so your team never
            worries about downtime. You pay a predictable monthly fee — nothing
            else.
          </p>
        </div>
        <ul className="grid gap-3">
          {CHECKS.map((c, i) => (
            <li key={i} className="card-premium p-5 flex items-center gap-3">
              <span className="w-8 h-8 rounded-full bg-[#3DDC97]/15 text-[#0A5F3F] flex items-center justify-center shrink-0">
                <Check size={16} />
              </span>
              <span className="text-sm font-medium">{c}</span>
            </li>
          ))}
        </ul>
      </section>

      {/* CTA */}
      <section className="max-w-7xl mx-auto px-5 md:px-8 pb-24">
        <div className="bg-brand-gradient rounded-[36px] p-10 md:p-16 text-center relative overflow-hidden">
          <div className="absolute inset-0 noise" />
          <div className="relative">
            <h2 className="font-display font-extrabold text-3xl md:text-5xl text-white tracking-tight">Ready to move your fleet?</h2>
            <p className="text-white/90 mt-4 max-w-xl mx-auto text-[15px] md:text-lg">
              Tell us about your city, expected usage and vehicle mix — we&apos;ll share a quote within 24 hours.
            </p>
            <div className="mt-8 flex flex-wrap justify-center gap-3">
              <button
                type="button"
                onClick={() => setEnquiryOpen(true)}
                data-testid="business-cta-open-form"
                className="inline-flex items-center gap-2 px-8 py-4 rounded-full bg-[#0A0B10] text-white font-bold hover:bg-black/80 transition-colors"
              >
                Enquire Fleet Solutions <ArrowRight size={16} />
              </button>
              <Link
                to="/contact"
                data-testid="business-cta-contact"
                className="inline-flex items-center gap-2 px-8 py-4 rounded-full bg-white text-[#0A0B10] font-bold hover:bg-white/90 transition-colors"
              >
                Contact form
              </Link>
            </div>
          </div>
        </div>
      </section>

      <FleetEnquiryModal open={enquiryOpen} onClose={() => setEnquiryOpen(false)} />
      <Footer />
    </div>
  );
};

export default BusinessFleet;
